import { cn } from '@noor/lib/cn';

type BroadcastFileKind = 'pdf' | 'image' | 'xls' | 'doc' | 'generic';

const KIND_BY_EXT: Record<string, BroadcastFileKind> = {
  pdf: 'pdf',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  xls: 'xls',
  xlsx: 'xls',
  csv: 'xls',
  doc: 'doc',
  docx: 'doc',
};

function broadcastFileKind(name: string | null | undefined): BroadcastFileKind {
  const ext = name?.split('.').pop()?.toLowerCase() ?? '';
  return KIND_BY_EXT[ext] ?? 'generic';
}

/**
 * Per-kind glyph color, in status tokens — the old panel's per-kind hexes
 * mapped onto the palette (teal→active and blue→active share; the glyph
 * shapes still tell image from document apart).
 */
const KIND_TONE: Record<BroadcastFileKind, string> = {
  pdf: 'text-status-danger',
  image: 'text-status-active',
  xls: 'text-status-success',
  doc: 'text-status-active',
  generic: 'text-status-neutral',
};

/**
 * The 20px attachment glyph, ported from the old panel's `fileIcon.tsx`:
 * a labeled document outline per kind, an image frame for pictures.
 */
export function BroadcastFileIcon({
  name,
  className,
}: {
  name: string | null | undefined;
  className?: string;
}) {
  const kind = broadcastFileKind(name);

  if (kind === 'image') {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-flex size-5 shrink-0', KIND_TONE[kind], className)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="4"
            width="18"
            height="16"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <circle cx="8.5" cy="9" r="1.6" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M4 17l4.5-4.5 3 3L15 11l5 5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }

  const label =
    kind === 'pdf' ? 'PDF' : kind === 'xls' ? 'XLS' : kind === 'doc' ? 'DOC' : '';

  return (
    <span
      aria-hidden="true"
      className={cn('inline-flex size-5 shrink-0', KIND_TONE[kind], className)}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M6 2.5h7L19 8v12a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V4a1.5 1.5 0 0 1 1.5-1.5z"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path
          d="M13 2.5V8h6"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        {label ? (
          <text
            x="12"
            y="17.5"
            textAnchor="middle"
            fontSize="5.2"
            fontWeight="700"
            fill="currentColor"
          >
            {label}
          </text>
        ) : (
          <path
            d="M8.5 13h7M8.5 16h7"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        )}
      </svg>
    </span>
  );
}
